import { FormEvent, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SearchFilters } from '@/components/research/SearchFilters';
import { LeadsTable } from '@/components/research/LeadsTable';
import type { Lead, SearchFilters as SearchFiltersType } from '@/types/research';
import { useResearchStore } from '@/stores/researchStore';
import { searchLeads } from '@/services/research';

type TestDriveCandidate = {
  searchResultId: string;
  name?: string;
  title?: string;
  company?: string;
  liUrl?: string;
  city?: string;
  state?: string;
  country?: string;
  companyCity?: string;
  companyState?: string;
  companyCountry?: string;
  industry?: string;
};

type TestDriveSearch = {
  companyName: string;
  title: string;
  seniority: string;
  department: string;
  industry: string;
  employeeSize: string;
  selectedCandidates: TestDriveCandidate[];
};

const MAX_SEARCHES = 3;
const DEFAULT_THEME: 'dark' | 'light' = 'dark';
const MAX_PREVIEW_PAGES = 3;

function clean(value: string): string {
  return value.trim();
}

function isValidEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
}

function toTestDriveSearch(filters: SearchFiltersType): TestDriveSearch {
  return {
    companyName: clean(filters.companies?.[0] || ''),
    title: clean(filters.titles?.[0] || ''),
    seniority: clean(filters.seniorities?.[0] || ''),
    department: clean(filters.departments?.[0] || ''),
    industry: clean(filters.industries?.[0] || ''),
    employeeSize: clean(filters.employeeSizes?.[0] || ''),
    selectedCandidates: [],
  };
}

function hasSearchCriteria(entry: TestDriveSearch): boolean {
  return Boolean(
    entry.companyName ||
    entry.title ||
    entry.seniority ||
    entry.department ||
    entry.industry ||
    entry.employeeSize
  );
}

export function TestDrive() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [consentEmail, setConsentEmail] = useState(false);
  const [consentCallText, setConsentCallText] = useState(false);
  const [priceRanges, setPriceRanges] = useState(''); // honeypot
  const [queuedSearches, setQueuedSearches] = useState<TestDriveSearch[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [previewStatus, setPreviewStatus] = useState('');
  const [activeQueuedIndex, setActiveQueuedIndex] = useState<number | null>(null);
  const [previewSelectedIds, setPreviewSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{
    requestId: string;
    table?: string;
    limitsBypassed?: boolean;
  } | null>(null);
  const [contactCredits, setContactCredits] = useState(999999);
  const {
    filters,
    setFilters,
    setLeads,
    setCurrentPage,
    setLoading,
    loading,
    leads,
    totalResults,
    currentPage,
  } = useResearchStore();

  const endpoint = useMemo(() => {
    const envApi = ((import.meta as any)?.env?.VITE_TEST_DRIVE_API as string | undefined)?.trim();
    const base = envApi || 'https://kohic3mczj.execute-api.us-east-1.amazonaws.com/prod';
    return `${base.replace(/\/$/, '')}/website/test-drive`;
  }, []);

  async function runPreviewSearch(activeFilters: SearchFiltersType, page: number) {
    setLoading(true);
    setPreviewStatus('');
    try {
      const cleanFilters = { ...activeFilters, contactSearchId: null, nextToken: null };
      const result = await searchLeads(cleanFilters, page, 25);
      setLeads(result.contacts, result.total);
      setFilters({ contactSearchId: result.contactSearchId, nextToken: result.nextToken });
      setCurrentPage(page);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setPreviewStatus(message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  async function addSearchFromFilters(nextFilters?: SearchFiltersType) {
    setError('');
    const source = nextFilters || filters;
    const entry = toTestDriveSearch(source);
    if (!hasSearchCriteria(entry)) {
      setError('Add at least one filter (for example: Industry).');
      return;
    }
    if (queuedSearches.length >= MAX_SEARCHES) {
      setError(`You can only queue ${MAX_SEARCHES} searches.`);
      return;
    }
    await runPreviewSearch(source, 0);
    setQueuedSearches((prev) => [...prev, entry]);
    setActiveQueuedIndex(queuedSearches.length);
    setPreviewSelectedIds(new Set());
  }

  function removeQueued(index: number) {
    setQueuedSearches((prev) => prev.filter((_, i) => i !== index));
    setActiveQueuedIndex((current) => {
      if (current == null) return current;
      if (current === index) return null;
      if (current > index) return current - 1;
      return current;
    });
  }

  function loadQueued(index: number) {
    const row = queuedSearches[index];
    if (!row) return;
    setFilters({
      companies: row.companyName ? [row.companyName] : [],
      titles: row.title ? [row.title] : [],
      seniorities: row.seniority ? [row.seniority] : [],
      departments: row.department ? [row.department] : [],
      industries: row.industry ? [row.industry] : [],
      employeeSizes: row.employeeSize ? [row.employeeSize] : [],
    });
    setPreviewStatus('');
    setActiveQueuedIndex(index);
    const preset = new Set((row.selectedCandidates || []).map((c) => String(c.searchResultId || '').trim()).filter(Boolean));
    setPreviewSelectedIds(preset);
  }

  async function viewQueued(index: number) {
    const row = queuedSearches[index];
    if (!row) return;
    const searchFilters: SearchFiltersType = {
      ...filters,
      companies: row.companyName ? [row.companyName] : [],
      titles: row.title ? [row.title] : [],
      seniorities: row.seniority ? [row.seniority] : [],
      departments: row.department ? [row.department] : [],
      industries: row.industry ? [row.industry] : [],
      employeeSizes: row.employeeSize ? [row.employeeSize] : [],
      page: 0,
    };
    setFilters(searchFilters);
    setActiveQueuedIndex(index);
    const preset = new Set((row.selectedCandidates || []).map((c) => String(c.searchResultId || '').trim()).filter(Boolean));
    setPreviewSelectedIds(preset);
    await runPreviewSearch(searchFilters, 0);
  }

  function togglePreviewSelection(lead: Lead) {
    setPreviewSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lead.searchResultId)) {
        next.delete(lead.searchResultId);
        return next;
      }
      if (next.size >= 5) return next;
      next.add(lead.searchResultId);
      return next;
    });
  }

  function addSelectedCandidatesToActiveSearch() {
    if (activeQueuedIndex == null || !queuedSearches[activeQueuedIndex]) {
      setError('Load or view a queued search first.');
      return;
    }
    const selected = leads.filter((lead) => previewSelectedIds.has(lead.searchResultId)).slice(0, 5);
    if (!selected.length) {
      setError('Select at least one person from results to add.');
      return;
    }
    const mapped: TestDriveCandidate[] = selected.map((lead: Lead) => ({
      searchResultId: lead.searchResultId,
      name: lead.name || [lead.firstName, lead.lastName].filter(Boolean).join(' '),
      title: lead.title || '',
      company: typeof lead.company === 'string' ? lead.company : (lead.company?.name || lead.companyName || lead.Company || ''),
      liUrl: lead.liUrl || lead.linkedInUrl || '',
      city: lead.city || '',
      state: lead.state || '',
      country: lead.country || '',
      companyCity: lead.companyCity || '',
      companyState: lead.companyState || '',
      companyCountry: lead.companyCountry || '',
      industry: lead.industry || '',
    }));
    setQueuedSearches((prev) =>
      prev.map((row, idx) => (idx === activeQueuedIndex ? { ...row, selectedCandidates: mapped } : row))
    );
    setError('');
  }

  async function handlePrevPage() {
    if (currentPage <= 0) return;
    await runPreviewSearch(filters, currentPage - 1);
  }

  async function handleNextPage() {
    const totalPages = Math.ceil(totalResults / 25) || 1;
    const maxAllowedPage = Math.min(totalPages, MAX_PREVIEW_PAGES) - 1;
    if (currentPage >= maxAllowedPage) return;
    await runPreviewSearch(filters, currentPage + 1);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSuccess(null);

    const cleanedName = clean(name);
    const cleanedEmail = clean(email).toLowerCase();
    const cleanedCompany = clean(company);
    const cleanedSearches = queuedSearches.slice(0, MAX_SEARCHES).filter((row) => hasSearchCriteria(row));

    if (!cleanedName) return setError('Name is required.');
    if (!cleanedEmail || !isValidEmail(cleanedEmail)) return setError('A valid email is required.');
    if (!cleanedCompany) return setError('Company name is required.');
    if (!consentEmail || !consentCallText) return setError('Both consent checkboxes are required.');
    if (!cleanedSearches.length) return setError('Queue at least one search.');
    if (cleanedSearches.some((row) => (row.selectedCandidates?.length || 0) === 0)) {
      return setError('Each queued search must include selected people from results.');
    }

    setSubmitting(true);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: cleanedName,
          email: cleanedEmail,
          company: cleanedCompany,
          consent_email_followup: true,
          consent_call_text_if_found: true,
          reportTheme: DEFAULT_THEME,
          price_ranges: priceRanges,
          searches: cleanedSearches,
        }),
      });

      const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      if (!response.ok || payload.success !== true) {
        setError(String(payload.error ?? `Request failed (${response.status})`));
        return;
      }

      setSuccess({
        requestId: String(payload.requestId ?? ''),
        table: String(payload.table ?? ''),
        limitsBypassed: payload.limits_bypassed === true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="bg-card-dark border border-border-dark rounded-xl p-6">
          <h1 className="text-3xl font-bold">Contact Search Test Drive</h1>
          <p className="text-gray-400 mt-2">
            Full contact-search filter interface is shown. In test-drive mode, non-allowed member filters are visible but disabled.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="rounded-md border border-border-dark bg-black/20 px-3 py-2">
              Searches per request: <span className="text-red-300 font-semibold">max 3</span>
            </div>
            <div className="rounded-md border border-border-dark bg-black/20 px-3 py-2">
              Pages per search: <span className="text-red-300 font-semibold">max 3</span>
            </div>
            <div className="rounded-md border border-border-dark bg-black/20 px-3 py-2">
              Results per page: <span className="text-red-300 font-semibold">25</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl overflow-hidden border border-border-dark">
              <SearchFilters
            onSearch={addSearchFromFilters}
            testDriveMode
            searchLabel="Search + Add"
          />
        </div>

        <div className="bg-card-dark border border-border-dark rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">
              Search Results Preview <span className="text-gray-500">({totalResults.toLocaleString()} total)</span>
            </h2>
            <span className="text-xs text-gray-400">Showing full page count, preview limited to first 3 pages.</span>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              onClick={addSelectedCandidatesToActiveSearch}
              disabled={activeQueuedIndex == null}
              className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 rounded text-xs font-semibold disabled:opacity-40"
            >
              Add Selected To Active Search (max 5)
            </button>
            <span className="text-xs text-gray-400">
              Active Search: {activeQueuedIndex == null ? 'None' : activeQueuedIndex + 1} | Selected in table: {previewSelectedIds.size}/5
            </span>
          </div>
          {previewStatus ? (
            <div className="rounded-md border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-200 mb-4">{previewStatus}</div>
          ) : null}
          <div className="rounded-lg border border-border-dark overflow-hidden">
            <LeadsTable
              onResearchComplete={() => {}}
              contactCredits={contactCredits}
              setContactCredits={setContactCredits}
              disableGetData
              getDataLabel="Locked"
              selectedReportIds={previewSelectedIds}
              onToggleReportSelection={togglePreviewSelection}
            />
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={handlePrevPage}
              disabled={loading || currentPage === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-300 hover:bg-gray-700 disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <div className="text-xs text-gray-300">
              Page <span className="text-white">{currentPage + 1}</span> / <span className="text-white">{Math.ceil(totalResults / 25) || 1}</span>
              <span className="ml-2 text-gray-500">(max preview page {MAX_PREVIEW_PAGES})</span>
            </div>
            <button
              onClick={handleNextPage}
              disabled={loading || currentPage >= Math.min(Math.ceil(totalResults / 25) || 1, MAX_PREVIEW_PAGES) - 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-300 hover:bg-gray-700 disabled:opacity-40"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="bg-card-dark border border-border-dark rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Queued Searches ({queuedSearches.length}/{MAX_SEARCHES})</h2>
            <button
              type="button"
              onClick={() => setQueuedSearches([])}
              className="text-xs px-3 py-1 rounded border border-border-dark hover:bg-black/20"
            >
              Clear Queue
            </button>
          </div>
          {!queuedSearches.length ? (
            <p className="text-sm text-gray-400">Use the filter panel above and click <span className="text-white">Add Search</span>.</p>
          ) : (
            <div className="space-y-2">
              {queuedSearches.map((row, idx) => (
                <div key={`${row.companyName}-${row.title}-${row.industry}-${idx}`} className="rounded-lg border border-border-dark bg-black/20 px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div><span className="text-gray-400">Company:</span> {row.companyName || 'Any'}</div>
                      <div><span className="text-gray-400">Title:</span> {row.title || 'Any'}</div>
                      <div className="text-xs text-gray-400">
                        Seniority: {row.seniority || 'Any'} | Department: {row.department || 'Any'} | Industry: {row.industry || 'Any'} | Employee Size: {row.employeeSize || 'Any'}
                      </div>
                      <div className="text-xs text-blue-300 mt-1">
                        Selected people for report: {row.selectedCandidates?.length || 0}/5
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => void viewQueued(idx)} className="text-xs px-2 py-1 rounded border border-blue-700 text-blue-300 hover:bg-blue-900/20">View</button>
                      <button type="button" onClick={() => loadQueued(idx)} className="text-xs px-2 py-1 rounded border border-border-dark hover:bg-black/20">Load</button>
                      <button type="button" onClick={() => removeQueued(idx)} className="text-xs px-2 py-1 rounded border border-red-700 text-red-300 hover:bg-red-900/20">Remove</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="bg-card-dark border border-border-dark rounded-xl p-5">
            <h2 className="text-lg font-semibold mb-3">Requester</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <label className="text-sm">
                <span className="mb-1 block text-gray-300">Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-border-dark bg-dark-bg px-3 py-2" required />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-gray-300">Email</span>
                <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-border-dark bg-dark-bg px-3 py-2" type="email" required />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-gray-300">Company Name</span>
                <input value={company} onChange={(e) => setCompany(e.target.value)} className="w-full rounded-md border border-border-dark bg-dark-bg px-3 py-2" required />
              </label>
            </div>
          </div>

          <input
            type="text"
            name="price_ranges"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            value={priceRanges}
            onChange={(e) => setPriceRanges(e.target.value)}
            className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden opacity-0"
          />

          <div className="space-y-2 rounded-xl border border-border-dark bg-card-dark p-4 text-sm">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={consentEmail} onChange={(e) => setConsentEmail(e.target.checked)} className="mt-0.5" />
              <span>I consent to follow-up by email.</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={consentCallText} onChange={(e) => setConsentCallText(e.target.checked)} className="mt-0.5" />
              <span>I consent to call/text follow-up if a phone number is found.</span>
            </label>
          </div>

          {error && <div className="rounded-md border border-rose-500/40 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">{error}</div>}
          {success && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200">
              Request accepted and queued. Request ID: <span className="font-mono">{success.requestId}</span>
              {success.table ? <span> | Backup table: <span className="font-mono">{success.table}</span></span> : null}
              {success.limitsBypassed ? <span> | Admin limits bypassed</span> : null}
            </div>
          )}

          <button type="submit" disabled={submitting} className="rounded-md bg-red-600 px-5 py-2 text-sm font-semibold hover:bg-red-500 disabled:opacity-50">
            {submitting ? 'Submitting...' : 'Submit Test Drive Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
